import { useEffect } from 'react';

/**
 * Lightweight per-route meta tag management for SEO.
 * Sets document.title, meta description, keywords, canonical, and Open Graph tags.
 * Restores defaults on unmount.
 */
const DEFAULTS = {
  title: 'ForwardsFlow | Full stack cashflow management.\u2122',
  description: 'Seven standalone services — yield discovery, exchange rates, compliance, hedging, liquidity, execution, portfolio — usable alone or as one cross-border cashflow platform across 6 markets.',
  canonical: 'https://www.forwardsflow.com/',
};

function setMeta(name, content, attr = 'name') {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

export default function useMetaTags({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  jsonLd,
  jsonLdId,
} = {}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    if (canonical) setCanonical(canonical);

    // Open Graph
    if (ogTitle || title) setMeta('og:title', ogTitle || title, 'property');
    if (ogDescription || description) setMeta('og:description', ogDescription || description, 'property');
    if (ogImage) setMeta('og:image', ogImage, 'property');
    if (ogType) setMeta('og:type', ogType, 'property');
    if (canonical) setMeta('og:url', canonical, 'property');

    // Twitter
    if (ogTitle || title) setMeta('twitter:title', ogTitle || title);
    if (ogDescription || description) setMeta('twitter:description', ogDescription || description);
    if (ogImage) setMeta('twitter:image', ogImage);

    // JSON-LD structured data
    if (jsonLd && jsonLdId) setJsonLd(jsonLdId, jsonLd);

    return () => {
      // Restore defaults on unmount
      document.title = DEFAULTS.title;
      setMeta('description', DEFAULTS.description);
      setCanonical(DEFAULTS.canonical);
      setMeta('og:title', DEFAULTS.title, 'property');
      setMeta('og:description', DEFAULTS.description, 'property');
      setMeta('og:url', DEFAULTS.canonical, 'property');
      setMeta('twitter:title', DEFAULTS.title);
      setMeta('twitter:description', DEFAULTS.description);
      if (jsonLdId) removeJsonLd(jsonLdId);
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogType, jsonLd, jsonLdId]);
}
